import { useAuth } from '@clerk/clerk-react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import {
  api,
  type RfqDetailResponse,
  type LineStage,
  type VendorQuoteListResponse,
  type VendorQuote,
  type ListCustomerQuotesResponse,
  type ClientQuote,
  type ClientQuoteStatus,
  type BisScreenStatus,
  type ListProformaInvoicesResponse,
  type ProformaInvoice,
  type ProformaInvoiceStatus,
  type PatchProformaResponse,
  type CardPaymentLinkResponse,
  type ListShipmentsResponse,
  type Shipment,
  type ShipmentStatus,
  type ListCommercialInvoicesResponse,
  type CommercialInvoice,
  type CommercialInvoiceStatus,
  type GetCommercialInvoiceResponse,
  type PatchRfqNotesResponse,
  type PatchLineItemNotesResponse,
  type ListVendorPosResponse,
  type VendorPurchaseOrder,
  type VendorPoStatus,
  type GetVendorPoResponse,
  type PatchVendorPoResponse,
  type DeleteVendorPoResponse,
} from '@/lib/api';
import { ParseVendorQuoteModal } from './parse-vendor-quote-modal';
import { LineItemEditModal, type LineItemInitial } from './line-item-edit-modal';
import { CustomerQuoteModal } from './customer-quote-modal';
import { EditQuoteModal } from './edit-quote-modal';
import { CombinedQuoteModal } from './combined-quote-modal';
import { AirworthinessCertField } from './airworthiness-cert-field';
import { CustomerPoCard } from './customer-po-card';
import { CustomerScreenChip } from './customer-screen-chip';
import { RfqReassignCustomerModal } from './rfq-reassign-customer-modal';
import { VendorPoModal } from './vendor-po-modal';
import { EditVendorPoModal } from './edit-vendor-po-modal';
import { EditVendorQuoteModal } from './edit-vendor-quote-modal';
import { ProformaInvoiceModal } from './proforma-invoice-modal';
import { ShipmentModal } from './shipment-modal';
import { CommercialInvoiceModal } from './commercial-invoice-modal';
import { ByPnRecommendations } from './by-pn-recommendations';
import { AltPartFinder } from './alt-part-finder';
import { PartsBaseListings } from './partsbase-listings';
import { EditableNotes } from './editable-notes';
import { CopyButton } from './copy-button';
import { DocumentLink } from './document-link';
import { phoneCountry, flagFromIso2 } from '@/lib/phone-country';
import { SourcingChannelChips } from './sourcing-channel-chips';

interface Props {
  publicId: string;
}

const STAGE_LABEL: Record<LineStage, string> = {
  received: 'Received',
  sourcing: 'Sourcing',
  quoted: 'Quoted',
  accepted: 'Accepted',
  po_sent: 'PO sent',
  invoiced: 'Invoiced',
  paid: 'Paid',
  shipped: 'Shipped',
  delivered: 'Delivered',
  reviewed: 'Reviewed',
  no_quote: 'No quote',
  lost: 'Lost',
};

export function RfqDetail({ publicId }: Props) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [showRaw, setShowRaw] = useState(false);
  const [parseHintLine, setParseHintLine] = useState<string | undefined>(undefined);
  const [parseOpen, setParseOpen] = useState(false);
  const [combineOpen, setCombineOpen] = useState(false);
  const [lineEditOpen, setLineEditOpen] = useState(false);
  const [lineEditMode, setLineEditMode] = useState<'edit' | 'create'>('edit');
  const [lineEditInitial, setLineEditInitial] = useState<LineItemInitial | undefined>(undefined);
  const [reassignOpen, setReassignOpen] = useState(false);

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['rfq', publicId],
    queryFn: () =>
      api<RfqDetailResponse>(`/api/rfqs/${publicId}`, { tokenGetter: () => getToken() }),
    staleTime: 10_000,
  });

  // Manual priority override (Standard / Urgent / AOG). Patches the two booleans
  // and refreshes the detail + list. AOG and Urgent are mutually exclusive here.
  const setPriority = useMutation({
    mutationFn: (level: 'standard' | 'urgent' | 'aog') => {
      const body =
        level === 'aog' ? { isAog: true, isUrgent: false }
        : level === 'urgent' ? { isAog: false, isUrgent: true }
        : { isAog: false, isUrgent: false };
      return api(`/api/rfqs/${publicId}`, {
        method: 'PATCH',
        tokenGetter: () => getToken(),
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rfq', publicId] });
      qc.invalidateQueries({ queryKey: ['rfqs'] });
    },
  });

  // Follow-up flag (stay on top of this RFQ). Toggling on opens a reason note;
  // toggling off clears it (server enforces the clear too).
  const setFlag = useMutation({
    mutationFn: (body: { isFlagged: boolean; flagNote?: string | null; flagSource?: 'manual' }) =>
      api(`/api/rfqs/${publicId}`, {
        method: 'PATCH',
        tokenGetter: () => getToken(),
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rfq', publicId] });
      qc.invalidateQueries({ queryKey: ['rfqs'] });
    },
  });

  // "Quote sent to customer" marker (RFQ-level, broker-controlled). Mirrors the
  // follow-up flag; visible on the RFQ list so quote status reads at a glance.
  const setQuoteSent = useMutation({
    mutationFn: (quoteSent: boolean) =>
      api(`/api/rfqs/${publicId}`, {
        method: 'PATCH',
        tokenGetter: () => getToken(),
        body: JSON.stringify({ quoteSent }),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rfq', publicId] });
      qc.invalidateQueries({ queryKey: ['rfqs'] });
    },
  });

  const { data: vqData } = useQuery({
    queryKey: ['vendor-quotes', { rfqId: publicId }],
    queryFn: () =>
      api<VendorQuoteListResponse>(`/api/vendor-quotes?rfqId=${publicId}`, {
        tokenGetter: () => getToken(),
      }),
    staleTime: 10_000,
  });

  const { data: cqData } = useQuery({
    queryKey: ['client-quotes', { rfqId: publicId }],
    queryFn: () =>
      api<ListCustomerQuotesResponse>(`/api/customer-quotes?rfqId=${publicId}`, {
        tokenGetter: () => getToken(),
      }),
    staleTime: 10_000,
  });

  const { data: piData } = useQuery({
    queryKey: ['proforma-invoices', { rfqId: publicId }],
    queryFn: () =>
      api<ListProformaInvoicesResponse>(`/api/proforma-invoices?rfqId=${publicId}`, {
        tokenGetter: () => getToken(),
      }),
    staleTime: 10_000,
  });

  const { data: shpData } = useQuery({
    queryKey: ['shipments', { rfqId: publicId }],
    queryFn: () =>
      api<ListShipmentsResponse>(`/api/shipments?rfqId=${publicId}`, {
        tokenGetter: () => getToken(),
      }),
    staleTime: 10_000,
  });

  const { data: ciData } = useQuery({
    queryKey: ['commercial-invoices', { rfqId: publicId }],
    queryFn: () =>
      api<ListCommercialInvoicesResponse>(`/api/commercial-invoices?rfqId=${publicId}`, {
        tokenGetter: () => getToken(),
      }),
    staleTime: 10_000,
  });

  const { data: vpoData } = useQuery({
    queryKey: ['vendor-purchase-orders', { rfqId: publicId }],
    queryFn: () =>
      api<ListVendorPosResponse>(`/api/vendor-purchase-orders?rfqId=${publicId}`, {
        tokenGetter: () => getToken(),
      }),
    staleTime: 10_000,
  });

  if (isLoading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  if (isError) {
    return (
      <div className="p-6 text-sm text-destructive">
        {(error as { message?: string })?.message ?? 'Failed to load RFQ'}
      </div>
    );
  }
  if (!data) return null;

  const { rfq, customer, contact, lineItems, sourcingPosts } = data;
  const postedChannels = sourcingPosts.map((p) => p.channel);

  return (
    <div className="h-full overflow-auto">
      <div className="px-6 py-5 border-b border-border">
        <div className="flex items-baseline gap-3 flex-wrap">
          <div className="font-mono text-lg font-semibold inline-flex items-center gap-1">
            {rfq.ref}
            <CopyButton value={rfq.ref} label={`Copy ${rfq.ref}`} />
          </div>
          <div className="text-xs text-muted-foreground">{rfq.id}</div>
          {/* Priority control — click to set Standard / Urgent / AOG. */}
          <div className="inline-flex rounded overflow-hidden border border-border text-xs" role="group" aria-label="RFQ priority">
            {(['standard', 'urgent', 'aog'] as const).map((lvl) => {
              const active =
                (lvl === 'aog' && rfq.isAog) ||
                (lvl === 'urgent' && rfq.isUrgent && !rfq.isAog) ||
                (lvl === 'standard' && !rfq.isAog && !rfq.isUrgent);
              const activeCls =
                lvl === 'aog' ? 'bg-destructive text-destructive-foreground'
                : lvl === 'urgent' ? 'bg-amber-500 text-white'
                : 'bg-muted text-foreground';
              return (
                <button
                  key={lvl}
                  type="button"
                  disabled={setPriority.isPending || active}
                  onClick={() => setPriority.mutate(lvl)}
                  title={`Mark ${lvl}`}
                  className={`px-2 py-0.5 font-semibold transition-colors ${
                    active ? activeCls : 'bg-transparent text-muted-foreground hover:bg-muted'
                  }`}
                >
                  {lvl === 'aog' ? 'AOG' : lvl === 'urgent' ? 'Urgent' : 'Standard'}
                </button>
              );
            })}
          </div>
          {setPriority.isError && (
            <span className="text-xs text-destructive">Couldn't update priority — try again</span>
          )}
          {/* Follow-up flag — stay on top of this RFQ, with an optional reason. */}
          <div className="inline-flex items-center gap-2">
            <button
              type="button"
              disabled={setFlag.isPending}
              onClick={() => setFlag.mutate({ isFlagged: !rfq.isFlagged })}
              title={
                rfq.isFlagged
                  ? 'Remove from the 🚩 Priority board'
                  : 'Add to the 🚩 Priority board'
              }
              className={`px-2 py-0.5 text-xs font-semibold rounded border transition-colors ${
                rfq.isFlagged
                  ? 'bg-orange-100 text-orange-900 border-orange-300'
                  : 'bg-transparent text-muted-foreground border-border hover:bg-muted'
              }`}
            >
              {/*
                Deliberately the BOARD's vocabulary, not the column's. This
                button is what puts an RFQ on the tab labelled "🚩 Priority",
                and while it said "Flag follow-up" it read as an unrelated
                feature — the control went unfound and the board went unused.
              */}
              {rfq.isFlagged ? '🚩 On priority board' : '⚐ Add to priority'}
            </button>
            {/*
              An AUTO flag (intake duplicate detection, or a customer reply
              adding lines) shows its badge but deliberately stays off the
              priority board — that list only holds what the broker chose.
              This promotes one onto the board while KEEPING its note, which
              toggling the flag off and on would destroy.
            */}
            {rfq.isFlagged && rfq.flagSource === 'auto' && (
              <button
                type="button"
                disabled={setFlag.isPending}
                onClick={() => setFlag.mutate({ isFlagged: true, flagSource: 'manual', flagNote: rfq.flagNote ?? null })}
                title="Auto-flagged by intake. Add it to your priority board."
                className="px-2 py-0.5 text-xs font-medium rounded border border-border
                           text-muted-foreground hover:bg-muted transition-colors"
              >
                📌 Add to priority
              </button>
            )}
            {rfq.isFlagged && (
              <input
                key={`flagnote-${rfq.flagNote ?? ''}`}
                type="text"
                defaultValue={rfq.flagNote ?? ''}
                placeholder="Reason (e.g. wants warranty terms + certs)"
                onBlur={(e) => {
                  const v = e.target.value.trim();
                  if (v !== (rfq.flagNote ?? '')) setFlag.mutate({ isFlagged: true, flagNote: v || null });
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                className="text-xs px-2 py-0.5 border border-border rounded w-full sm:w-72 bg-background"
              />
            )}
            {setFlag.isError && (
              <span className="text-xs text-destructive">Couldn't update flag — try again</span>
            )}
          </div>
          {/* Quote-sent marker — toggle on once the customer has been quoted. */}
          <div className="inline-flex items-center gap-2">
            <button
              type="button"
              disabled={setQuoteSent.isPending}
              onClick={() => setQuoteSent.mutate(!rfq.quoteSent)}
              title={rfq.quoteSent
                ? `Quote sent${rfq.quoteSentAt ? ` ${new Date(rfq.quoteSentAt).toLocaleDateString()}` : ''} — click to unset`
                : 'Mark that a quote has been sent to the customer'}
              className={`px-2 py-0.5 text-xs font-semibold rounded border transition-colors ${
                rfq.quoteSent
                  ? 'bg-emerald-100 text-emerald-900 border-emerald-300'
                  : 'bg-transparent text-muted-foreground border-border hover:bg-muted'
              }`}
            >
              {rfq.quoteSent
                ? `✅ Quote sent${rfq.quoteSentAt ? ` · ${new Date(rfq.quoteSentAt).toLocaleDateString()}` : ''}`
                : '✉️ Mark quote sent'}
            </button>
            {setQuoteSent.isError && (
              <span className="text-xs text-destructive">Couldn't update — try again</span>
            )}
          </div>
        </div>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <span className="text-muted-foreground text-xs uppercase tracking-wide">Posted to</span>
          <SourcingChannelChips
            rfqPublicId={publicId}
            postedChannels={postedChannels}
            variant="interactive"
            invalidateKeys={customer ? [['customer', customer.id]] : []}
          />
        </div>
        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
          <div className="min-w-0">
            <span className="text-muted-foreground text-xs uppercase tracking-wide">Customer</span>
            <div className="flex items-center gap-2 flex-wrap">
              <span>{customer?.displayName ?? customer?.legalName ?? <span className="italic text-muted-foreground">Unknown</span>}</span>
              <button
                type="button"
                onClick={() => setReassignOpen(true)}
                className="text-xs px-1.5 py-0.5 border border-border rounded hover:bg-muted text-muted-foreground"
                title="Reassign this RFQ to a different customer"
              >
                Change
              </button>
              {(() => {
                // Country flag for an at-a-glance read of where the customer is.
                // Prefer the contact phone's country (matches the Customers view),
                // fall back to the customer's stored ISO-2 country code.
                const info = phoneCountry(contact?.phone);
                const flag = info?.flag ?? flagFromIso2(customer?.countryCode);
                const label = info?.name ?? customer?.countryCode ?? undefined;
                return flag ? (
                  <span className="text-base leading-none" title={label} aria-label={label}>
                    {flag}
                  </span>
                ) : null;
              })()}
              {customer && (
                <CustomerScreenChip
                  customerPublicId={customer.id}
                  customerLegalName={customer.legalName}
                  customerCountryCode={customer.countryCode}
                  rfqPublicId={publicId}
                  bisScreenStatus={customer.bisScreenStatus}
                  bisLastScreenedAt={customer.bisLastScreenedAt}
                  bisScreenNotes={customer.bisScreenNotes}
                  bisScreenPdfKey={customer.bisScreenPdfKey}
                  bisScreenPdfUrl={customer.bisScreenPdfUrl}
                  bisScreenDocumentFilename={customer.bisScreenDocumentFilename}
                  bisScreenDocumentSha256={customer.bisScreenDocumentSha256}
                />
              )}
            </div>
          </div>
          <div className="min-w-0">
            <span className="text-muted-foreground text-xs uppercase tracking-wide">Contact</span>
            {/* break-words: an email is one unbreakable token — without this it
                sets the cell's min-content width and widens the whole pane. */}
            <div className="break-words">
              {contact ? (
                <>
                  {contact.fullName ?? contact.email}
                  {contact.fullName && <span className="text-muted-foreground"> · {contact.email}</span>}
                </>
              ) : (
                <span className="italic text-muted-foreground">—</span>
              )}
            </div>
          </div>
          <div>
            <span className="text-muted-foreground text-xs uppercase tracking-wide">Source</span>
            <div>{rfq.sourceChannel ?? '—'}</div>
          </div>
          <div>
            <span className="text-muted-foreground text-xs uppercase tracking-wide">Currency · Incoterms</span>
            <div>{rfq.defaultCurrency ?? '—'} · {rfq.defaultIncoterms ?? '—'}</div>
          </div>
          {rfq.parserModel && (
            <div className="col-span-2">
              <span className="text-muted-foreground text-xs uppercase tracking-wide">Parser</span>
              <div>
                {rfq.parserModel}
                {rfq.parserConfidence !== null && (
                  <span className="text-muted-foreground"> · {(rfq.parserConfidence * 100).toFixed(0)}% confidence</span>
                )}
              </div>
            </div>
          )}
        </div>
        {rfq.sourceRawText && (
          <details className="mt-3" open={showRaw} onToggle={(e) => setShowRaw((e.target as HTMLDetailsElement).open)}>
            <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
              Show raw email source
            </summary>
            <pre className="mt-2 p-3 bg-muted text-xs font-mono whitespace-pre-wrap rounded border border-border max-h-64 overflow-auto">
              {rfq.sourceRawText}
            </pre>
          </details>
        )}

        <EditableNotes
          value={rfq.notes}
          label="RFQ notes (broker)"
          placeholder="No notes yet — click Add to log context, vendor decisions, customer pushback, etc."
          onSave={async (newValue, getToken) => {
            await api<PatchRfqNotesResponse>(`/api/rfqs/${publicId}`, {
              method: 'PATCH',
              tokenGetter: getToken,
              body: JSON.stringify({ notes: newValue }),
            });
          }}
          onSaved={() => qc.invalidateQueries({ queryKey: ['rfq', publicId] })}
        />

        {/* Customer PO — first-class event captured before proforma issuance. */}
        <CustomerPoCard
          rfqPublicId={publicId}
          customerPoNumber={rfq.customerPoNumber}
          customerPoReceivedAt={rfq.customerPoReceivedAt}
          customerPoDocumentKey={rfq.customerPoDocumentKey}
          customerPoDocumentUrl={rfq.customerPoDocumentUrl}
          customerPoDocumentFilename={rfq.customerPoDocumentFilename}
          customerPoDocumentSha256={rfq.customerPoDocumentSha256}
          customerPoNotes={rfq.customerPoNotes}
        />
      </div>

      <div className="px-6 py-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-xs uppercase tracking-wide text-muted-foreground">
            Line items ({lineItems.length})
          </h3>
          <div className="flex items-center gap-2">
            {(cqData?.clientQuotes?.length ?? 0) > 1 && (
              <button
                onClick={() => setCombineOpen(true)}
                className="px-2.5 py-1 text-xs font-medium border border-border rounded hover:bg-muted"
              >
                Combine into one quote ({cqData?.clientQuotes?.length})
              </button>
            )}
            <button
              onClick={() => {
                setLineEditMode('create');
                setLineEditInitial(undefined);
                setLineEditOpen(true);
              }}
              className="px-2.5 py-1 text-xs font-medium border border-border rounded hover:bg-muted"
            >
              + Add line item
            </button>
            <button
              onClick={() => { setParseHintLine(undefined); setParseOpen(true); }}
              className="px-2.5 py-1 text-xs font-medium border border-border rounded hover:bg-muted"
            >
              + Parse vendor quote
            </button>
          </div>
        </div>
        <ul className="space-y-2">
          {lineItems.map((li) => {
            const lineQuotes = (vqData?.vendorQuotes ?? []).filter((vq) => vq.rfqLineItemId === li.id);
            const lineClientQuotes = (cqData?.clientQuotes ?? []).filter((cq) => cq.rfqLineItemId === li.id);
            const lineProformas = (piData?.proformaInvoices ?? []).filter((pi) => pi.rfqLineItemId === li.id);
            const lineShipments = (shpData?.shipments ?? []).filter((shp) => shp.rfqLineItemId === li.id);
            const lineCis = (ciData?.commercialInvoices ?? []).filter((ci) => ci.rfqLineItemId === li.id);
            const lineVendorPos = (vpoData?.vendorPurchaseOrders ?? []).filter((p) => p.rfqLineItemId === li.id);
            return (
              <li key={li.id} className="border border-border rounded-md p-3">
                <div className="flex items-baseline justify-between gap-2 flex-wrap">
                  <div className="font-mono text-sm font-medium flex items-center gap-1 min-w-0 flex-wrap">
                    <span>#{li.lineNumber} ·</span>
                    <span className="inline-flex items-center gap-1">
                      {li.partNumber}
                      <CopyButton value={li.partNumber} label={`Copy ${li.partNumber}`} size="sm" />
                    </span>
                    {li.altPartNumber && (
                      <span className="text-muted-foreground inline-flex items-center gap-1">
                        / {li.altPartNumber}
                        <CopyButton value={li.altPartNumber} label={`Copy ${li.altPartNumber}`} size="sm" />
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-sm text-muted-foreground">
                      qty {li.qty} {li.uom}
                    </div>
                    <button
                      onClick={() => {
                        setLineEditMode('edit');
                        setLineEditInitial({
                          publicId: li.id,
                          lineNumber: li.lineNumber,
                          partNumber: li.partNumber,
                          altPartNumber: li.altPartNumber ?? null,
                          description: li.description ?? null,
                          manufacturer: li.manufacturer ?? null,
                          aircraftPlatform: li.aircraftPlatform ?? null,
                          qty: li.qty,
                          uom: li.uom,
                          conditionRequested: li.conditionRequested ?? null,
                          certRequested: li.certRequested ?? null,
                          isAog: li.isAog,
                          notes: li.notes ?? null,
                        });
                        setLineEditOpen(true);
                      }}
                      className="px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground hover:bg-muted rounded border border-border"
                      title="Edit line item"
                    >
                      Edit
                    </button>
                  </div>
                </div>
                {(li.description || li.manufacturer || li.aircraftPlatform) && (
                  <div className="text-xs text-muted-foreground mt-1">
                    {[li.description, li.manufacturer, li.aircraftPlatform].filter(Boolean).join(' · ')}
                  </div>
                )}
                <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                  <span className="px-1.5 py-0.5 bg-accent text-accent-foreground rounded font-medium">
                    {STAGE_LABEL[li.stage]}
                  </span>
                  {li.conditionRequested && (
                    <span className="px-1.5 py-0.5 bg-muted rounded">cond {li.conditionRequested}</span>
                  )}
                  {li.certRequested && (
                    <span className="px-1.5 py-0.5 bg-muted rounded">{li.certRequested.replace(/_/g, ' ')}</span>
                  )}
                  {li.isAog && (
                    <span className="px-1.5 py-0.5 bg-destructive text-destructive-foreground rounded">AOG</span>
                  )}
                  {li.itarControlled && (
                    <span className="px-1.5 py-0.5 bg-amber-100 text-amber-900 rounded">ITAR</span>
                  )}
                  {li.eccn && <span className="px-1.5 py-0.5 bg-muted rounded">ECCN {li.eccn}</span>}
                  {li.htsCode && <span className="px-1.5 py-0.5 bg-muted rounded">HTS {li.htsCode}</span>}
                  {li.hazmatClass && (
                    <span className="px-1.5 py-0.5 bg-amber-100 text-amber-900 rounded">Hazmat {li.hazmatClass}</span>
                  )}
                  {li.traceabilityRequired && (
                    <span className="px-1.5 py-0.5 bg-muted rounded">trace required</span>
                  )}
                </div>
                <AirworthinessCertField
                  lineId={li.id}
                  rfqPublicId={publicId}
                  certKey={li.airworthinessCertKey}
                  certUrl={li.airworthinessCertUrl}
                  certFilename={li.airworthinessCertFilename}
                  certSha256={li.airworthinessCertSha256}
                  certUploadedAt={li.airworthinessCertUploadedAt}
                />
                <EditableNotes
                  value={li.notes}
                  compact
                  label={`Line ${li.lineNumber} notes`}
                  placeholder="No notes for this line yet — log condition specifics, vendor pushback, etc."
                  maxLength={2000}
                  onSave={async (newValue, getToken) => {
                    await api<PatchLineItemNotesResponse>(`/api/rfqs/line-items/${li.id}`, {
                      method: 'PATCH',
                      tokenGetter: getToken,
                      body: JSON.stringify({ notes: newValue }),
                    });
                  }}
                  onSaved={() => qc.invalidateQueries({ queryKey: ['rfq', publicId] })}
                />

                {/* Recommended vendors (collapsed by default) */}
                <ByPnRecommendations partNumber={li.partNumber} platform={li.aircraftPlatform} />

                {/* DLA FLIS alternate part-number finder (on-demand, while sourcing) */}
                <AltPartFinder
                  rfqPublicId={publicId}
                  lineItemPublicId={li.id}
                  partNumber={li.partNumber}
                  stage={li.stage}
                />

                {/* Live PartsBase seller listings (read-only; sends nothing) */}
                <PartsBaseListings
                  rfqPublicId={publicId}
                  lineItemPublicId={li.id}
                  partNumber={li.partNumber}
                  stage={li.stage}
                />

                {/* Vendor quotes attached to this line */}
                <div className="mt-3 pt-3 border-t border-border space-y-1.5">
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      Vendor quotes ({lineQuotes.length})
                    </div>
                    <button
                      onClick={() => { setParseHintLine(li.id); setParseOpen(true); }}
                      className="text-xs font-medium text-primary hover:underline"
                    >
                      + Parse for this line
                    </button>
                  </div>
                  {lineQuotes.length === 0 ? (
                    <div className="text-xs text-muted-foreground italic">No quotes yet.</div>
                  ) : (
                    <ul className="space-y-1.5">
                      {lineQuotes.map((vq) => (
                        <VendorQuoteRow
                          key={vq.id}
                          vq={vq}
                          lineId={li.id}
                          lineQty={li.qty}
                          rfqId={publicId}
                          customerName={customer?.displayName ?? customer?.legalName ?? null}
                          customerCountryCode={customer?.countryCode ?? null}
                          lineCertRequested={li.certRequested}
                        />
                      ))}
                    </ul>
                  )}
                </div>

                {/* Customer quotes (committed) attached to this line */}
                {lineClientQuotes.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border space-y-1.5">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      Customer quotes ({lineClientQuotes.length})
                    </div>
                    <ul className="space-y-1.5">
                      {lineClientQuotes.map((cq) => {
                        // Match on quote identity, not ref: re-quoting a line
                        // reuses the same ref, so a ref match would tie one
                        // proforma to every re-quote of that line.
                        const cqProformas = lineProformas.filter((pi) => pi.clientQuoteId === cq.id);
                        // Only a live proforma means "already invoiced". An
                        // expired/cancelled one is a dead document and must not
                        // block issuing a replacement.
                        const hasLiveProforma = cqProformas.some(
                          (pi) => pi.status !== 'expired' && pi.status !== 'cancelled',
                        );
                        return (
                          <ClientQuoteRow
                            key={cq.id}
                            cq={cq}
                            rfqId={publicId}
                            hasProforma={hasLiveProforma}
                            rfqCustomerPoNumber={rfq.customerPoNumber}
                            customerCountryCode={customer?.countryCode ?? null}
                            customerBisScreenStatus={customer?.bisScreenStatus ?? null}
                          />
                        );
                      })}
                    </ul>
                  </div>
                )}

                {/* Vendor POs attached to this line */}
                {lineVendorPos.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border space-y-1.5">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      Vendor POs ({lineVendorPos.length})
                    </div>
                    <ul className="space-y-1.5">
                      {lineVendorPos.map((vpo) => (
                        <VendorPoRow key={vpo.id} vpo={vpo} rfqId={publicId} />
                      ))}
                    </ul>
                  </div>
                )}

                {/* Proforma invoices attached to this line */}
                {lineProformas.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border space-y-1.5">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      Proforma invoices ({lineProformas.length})
                    </div>
                    <ul className="space-y-1.5">
                      {lineProformas.map((pi) => {
                        const piShipments = lineShipments.filter((shp) => shp.proformaInvoiceId === pi.id);
                        return (
                          <ProformaRow
                            key={pi.id}
                            pi={pi}
                            rfqId={publicId}
                            hasShipment={piShipments.length > 0}
                            sourceQuote={lineClientQuotes.find((cq) => cq.ref === pi.quoteRef) ?? null}
                            rfqCustomerPoNumber={rfq.customerPoNumber}
                            customerCountryCode={customer?.countryCode ?? null}
                            customerBisScreenStatus={customer?.bisScreenStatus ?? null}
                          />
                        );
                      })}
                    </ul>
                  </div>
                )}

                {/* Shipments attached to this line */}
                {lineShipments.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border space-y-1.5">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      Shipments ({lineShipments.length})
                    </div>
                    <ul className="space-y-1.5">
                      {lineShipments.map((shp) => {
                        const shpCis = lineCis.filter((ci) => ci.shipmentRef === shp.ref);
                        const proforma = lineProformas.find((pi) => pi.ref === shp.proformaRef) ?? null;
                        return (
                          <ShipmentRow
                            key={shp.id}
                            shp={shp}
                            proforma={proforma}
                            rfqId={publicId}
                            hasCommercialInvoice={shpCis.length > 0}
                          />
                        );
                      })}
                    </ul>
                  </div>
                )}

                {/* Commercial invoices attached to this line */}
                {lineCis.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border space-y-1.5">
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      Commercial invoices ({lineCis.length})
                    </div>
                    <ul className="space-y-1.5">
                      {lineCis.map((ci) => (
                        <CommercialInvoiceRow key={ci.id} ci={ci} />
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      <ParseVendorQuoteModal
        open={parseOpen}
        onClose={() => setParseOpen(false)}
        rfqId={publicId}
        hintRfqLineItemId={parseHintLine}
      />

      <LineItemEditModal
        open={lineEditOpen}
        onClose={() => setLineEditOpen(false)}
        mode={lineEditMode}
        rfqId={publicId}
        initial={lineEditInitial}
      />

      <CombinedQuoteModal
        open={combineOpen}
        onClose={() => setCombineOpen(false)}
        rfqId={publicId}
        clientQuotes={cqData?.clientQuotes ?? []}
        partNumberByLineId={Object.fromEntries(lineItems.map((li) => [li.id, li.partNumber]))}
      />

      {reassignOpen && (
        <RfqReassignCustomerModal
          rfqPublicId={publicId}
          currentCustomerName={customer?.displayName ?? customer?.legalName ?? null}
          currentCustomerPublicId={customer?.id ?? null}
          // Every issued document froze the buyer's name into its snapshot.
          // Count accepted as well as sent — an accepted quote is won revenue,
          // the case where a silent re-attribution hurts most.
          issuedDocCount={
            (cqData?.clientQuotes ?? []).filter(
              (cq) => cq.status === 'sent' || cq.status === 'accepted',
            ).length +
            (piData?.proformaInvoices ?? []).length +
            (ciData?.commercialInvoices ?? []).length
          }
          onClose={() => setReassignOpen(false)}
        />
      )}
    </div>
  );
}

const CERT_LABELS_BY_CODE: Record<string, string> = {
  faa_8130_3: 'FAA Form 8130-3',
  easa_form_1: 'EASA Form 1',
  dual_release: 'Dual release (FAA 8130-3 + EASA Form 1)',
  coc: 'Certificate of Conformance',
  none: 'No airworthiness paperwork',
};

interface VendorQuoteRowProps {
  vq: VendorQuote;
  lineId: string;
  lineQty: number;
  rfqId: string;
  customerName: string | null;
  customerCountryCode: string | null;
  lineCertRequested: string | null;
}

function VendorQuoteRow({ vq, lineId, lineQty, rfqId, customerName, customerCountryCode, lineCertRequested }: VendorQuoteRowProps) {
  const total = (vq.unitPriceCents * vq.qtyOffered) / 100;
  const unit = vq.unitPriceCents / 100;
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [poOpen, setPoOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const certCode = vq.certOffered ?? lineCertRequested ?? null;
  const defaultCertLabel = certCode ? (CERT_LABELS_BY_CODE[certCode] ?? certCode) : null;
  return (
    <li
      className={`rounded p-2 text-sm ${
        // The vendor the customer was actually quoted from reads at a glance,
        // not only from a pill. A line carries five or six of these.
        (vq.quotedOnRefs?.length ?? 0) > 0
          ? 'bg-success-muted/50 border border-[hsl(var(--success))]/30'
          : 'bg-muted/40'
      }`}
    >
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="font-medium flex items-center gap-1.5 flex-wrap">
          {vq.vendorNameRaw}
          {/* Which vendor did we actually quote the customer from? A line
              collects five or six quotes and the link has always existed on
              client_quotes.vendor_quote_id — nothing displayed it, so finding
              out meant opening each customer quote. */}
          {(vq.quotedOnRefs?.length ?? 0) > 0 && (
            <span
              className="pill pill-success text-[9px]"
              title={`Customer was quoted from this vendor — ${vq.quotedOnRefs!.join(', ')}${
                vq.quotedAt ? ` (${new Date(vq.quotedAt).toLocaleDateString()})` : ''
              }`}
            >
              Quoted to customer
            </span>
          )}
          {(vq.poRefs?.length ?? 0) > 0 && (
            <span
              className="pill pill-primary text-[9px]"
              title={`Vendor PO raised — ${vq.poRefs!.join(', ')}`}
            >
              PO raised
            </span>
          )}
        </div>
        <div className="flex items-baseline gap-3">
          <div className="font-mono">
            {vq.currency} {unit.toFixed(2)} × {vq.qtyOffered}
            {vq.qtyOffered > 1 && (
              <span className="text-muted-foreground"> = {vq.currency} {total.toFixed(2)}</span>
            )}
          </div>
          <button
            onClick={() => setQuoteOpen(true)}
            className="text-xs font-medium text-primary hover:underline"
          >
            Generate quote →
          </button>
          <button
            onClick={() => setPoOpen(true)}
            className="text-xs font-medium text-primary hover:underline"
            title="Generate APD's PO to this vendor (after customer commitment)"
          >
            Generate vendor PO →
          </button>
          {/* Committed vendor quotes had no edit path at all until now, and 98%
              of them were written by the parser. */}
          <button
            onClick={() => setEditOpen(true)}
            className="text-xs font-medium text-foreground-muted hover:text-primary hover:underline"
            title="Correct the price, contact, lead time or compliance codes on this quote"
          >
            Edit
          </button>
        </div>
      </div>
      <div className="mt-1 flex flex-wrap gap-1.5 text-xs">
        {vq.conditionOffered && (
          <span className="px-1.5 py-0.5 bg-background rounded">{vq.conditionOffered}</span>
        )}
        {vq.certOffered && (
          <span className="px-1.5 py-0.5 bg-background rounded">{vq.certOffered.replace(/_/g, ' ')}</span>
        )}
        {vq.leadTimeDays != null && (
          <span className="px-1.5 py-0.5 bg-background rounded">{vq.leadTimeDays}d lead</span>
        )}
        {vq.incoterms && (
          <span className="px-1.5 py-0.5 bg-background rounded">{vq.incoterms}{vq.fobLocation ? ` ${vq.fobLocation}` : ''}</span>
        )}
        {vq.source !== 'manual' && (
          <span className="px-1.5 py-0.5 bg-accent text-accent-foreground rounded">
            AI {vq.parserConfidence != null ? `${(vq.parserConfidence * 100).toFixed(0)}%` : ''}
          </span>
        )}
        {(vq.rawSourceKey || vq.rawSourceUrl) && (
          <DocumentLink
            objectKey={vq.rawSourceKey}
            fallbackUrl={vq.rawSourceUrl}
            label="PDF ↗"
            filename={`vendor-quote-${vq.id}.pdf`}
            className="px-1.5 py-0.5 bg-background rounded text-primary hover:underline"
          />
        )}
      </div>
      {vq.notes && <div className="text-xs text-muted-foreground mt-1 italic">{vq.notes}</div>}

      <CustomerQuoteModal
        open={quoteOpen}
        onClose={() => setQuoteOpen(false)}
        rfqLineItemId={lineId}
        rfqId={rfqId}
        vendorQuote={vq}
        lineQty={lineQty}
      />

      <VendorPoModal
        open={poOpen}
        onClose={() => setPoOpen(false)}
        rfqLineItemId={lineId}
        rfqId={rfqId}
        vendorQuotePublicId={vq.id}
        defaultQty={lineQty}
        vendorNameRaw={vq.vendorNameRaw}
        vendorUnitPriceCents={vq.unitPriceCents}
        currency={vq.currency}
        customerName={customerName}
        customerCountryCode={customerCountryCode}
        defaultCertLabel={defaultCertLabel}
      />

      {editOpen && (
        <EditVendorQuoteModal quote={vq} onClose={() => setEditOpen(false)} />
      )}
    </li>
  );
}

const STATUS_BADGE: Record<ClientQuoteStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  sent: 'bg-blue-100 text-blue-900',
  accepted: 'bg-emerald-100 text-emerald-900',
  rejected: 'bg-red-100 text-red-900',
  expired: 'bg-amber-100 text-amber-900',
};

const CURRENCY_SYMBOL: Record<string, string> = {
  USD: '$', EUR: '€', GBP: '£', CAD: 'CA$', AUD: 'A$',
  JPY: '¥', CHF: 'CHF ', AED: 'AED ', SGD: 'S$',
};

function fmtMoney(cents: number, currency: string): string {
  const sym = CURRENCY_SYMBOL[currency] ?? `${currency} `;
  if (currency === 'JPY') return `${sym}${Math.round(cents / 100).toLocaleString('en-US')}`;
  return `${sym}${(cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}

function ClientQuoteRow({
  cq,
  rfqId,
  hasProforma,
  rfqCustomerPoNumber,
  customerCountryCode,
  customerBisScreenStatus,
}: {
  cq: ClientQuote;
  rfqId: string;
  hasProforma: boolean;
  rfqCustomerPoNumber: string | null;
  customerCountryCode: string | null;
  customerBisScreenStatus: import('@/lib/api').BisScreenStatus | null;
}) {
  const isExpired = cq.status === 'sent' && new Date(cq.validUntil) < new Date();
  const [proformaOpen, setProformaOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  return (
    <li className="bg-blue-50/40 rounded p-2 text-sm border border-blue-100">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="font-mono text-xs font-semibold">
          {cq.ref}
          <span className={`ml-2 px-1.5 py-0.5 text-[10px] uppercase tracking-wide rounded ${STATUS_BADGE[cq.status]}`}>
            {cq.status}
          </span>
          {isExpired && (
            <span className="ml-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wide rounded bg-amber-100 text-amber-900">
              past validity
            </span>
          )}
        </div>
        <div className="flex items-baseline gap-3">
          <div className="font-mono">
            {fmtMoney(cq.customerTotalCents, cq.currency)}
            {cq.qty > 1 && (
              <span className="text-muted-foreground"> ({cq.qty} ea)</span>
            )}
          </div>
          {/* Re-open the quote: drafts are editable (regenerate / mark sent /
              delete), non-drafts open read-only to view / download / copy. */}
          <button
            onClick={() => setEditOpen(true)}
            className="text-xs font-medium text-primary hover:underline"
          >
            {cq.status === 'draft' ? 'Edit' : 'View'}
          </button>
          {/* Once a quote is accepted, the natural next step is to issue a
              proforma. Hide the button if a proforma already exists for
              this quote — clicking through the existing one is faster. */}
          {cq.status === 'accepted' && !hasProforma && (
            <button
              onClick={() => setProformaOpen(true)}
              className="text-xs font-medium text-primary hover:underline"
            >
              Issue proforma →
            </button>
          )}
        </div>
      </div>

      <EditQuoteModal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        quote={cq}
        rfqId={rfqId}
      />

      <div className="mt-1 flex flex-wrap gap-1.5 text-[11px] text-muted-foreground">
        <span>markup {cq.markupPct}% ({cq.markupSource})</span>
        <span>·</span>
        <span>generated {new Date(cq.generatedAt).toLocaleDateString()}</span>
        {cq.sentAt && <><span>·</span><span>sent {new Date(cq.sentAt).toLocaleDateString()}</span></>}
        {cq.acceptedAt && <><span>·</span><span>accepted {new Date(cq.acceptedAt).toLocaleDateString()}</span></>}
        <span>·</span>
        <span>valid until {new Date(cq.validUntil).toLocaleDateString()}</span>
      </div>

      <ProformaInvoiceModal
        open={proformaOpen}
        onClose={() => setProformaOpen(false)}
        clientQuote={cq}
        rfqId={rfqId}
        rfqCustomerPoNumber={rfqCustomerPoNumber}
        customerCountryCode={customerCountryCode}
        customerBisScreenStatus={customerBisScreenStatus}
      />
    </li>
  );
}

const PROFORMA_STATUS_BADGE: Record<ProformaInvoiceStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  sent: 'bg-blue-100 text-blue-900',
  paid: 'bg-emerald-100 text-emerald-900',
  cancelled: 'bg-red-100 text-red-900',
  expired: 'bg-amber-100 text-amber-900',
};

function ProformaRow({
  pi,
  rfqId,
  hasShipment,
  sourceQuote,
  rfqCustomerPoNumber,
  customerCountryCode,
  customerBisScreenStatus,
}: {
  pi: ProformaInvoice;
  rfqId: string;
  hasShipment: boolean;
  sourceQuote: ClientQuote | null;
  rfqCustomerPoNumber: string | null;
  customerCountryCode: string | null;
  customerBisScreenStatus: BisScreenStatus | null;
}) {
  // 'expired' renders its own badge, so only flag a still-open proforma whose
  // validity has lapsed but the expiry cron hasn't caught yet.
  const isPastValid =
    (pi.status === 'sent' || pi.status === 'draft') && new Date(pi.validUntil) < new Date();
  // A proforma stays actionable until it's paid or deliberately cancelled.
  // Expiry is just the validity clock lapsing — the customer can still wire the
  // money, and the broker must be able to record that. Without 'expired' here,
  // an expired proforma is a dead end: no way to pay it, and no way to issue a
  // replacement either. The backend PATCH imposes no status guard; this is
  // purely a client-side gate.
  const canResolve = pi.status === 'draft' || pi.status === 'sent' || pi.status === 'expired';
  const [shipmentOpen, setShipmentOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [payOpen, setPayOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('wire');
  const [paymentReference, setPaymentReference] = useState('');

  // Mark an open proforma paid (or cancelled) directly from the RFQ.
  // Mirrors the proforma modal's status panel — needed because that panel only
  // shows in the same session a proforma is created; a proforma reopened
  // later otherwise has no path to 'paid'.
  const patchMutation = useMutation({
    mutationFn: async (newStatus: 'paid' | 'cancelled') => {
      const body: Record<string, unknown> = { status: newStatus };
      if (newStatus === 'paid') {
        body.paymentMethod = paymentMethod;
        if (paymentReference) body.paymentReference = paymentReference;
      }
      return api<PatchProformaResponse>(`/api/proforma-invoices/${pi.id}`, {
        method: 'PATCH',
        tokenGetter: () => getToken(),
        body: JSON.stringify(body),
      });
    },
    onSuccess: () => {
      setPayOpen(false);
      qc.invalidateQueries({ queryKey: ['proforma-invoices'] });
      qc.invalidateQueries({ queryKey: ['client-quotes'] });
      qc.invalidateQueries({ queryKey: ['rfq', rfqId] });
      qc.invalidateQueries({ queryKey: ['rfqs'] });
    },
  });

  // Card payment link (Stripe Checkout) for urgent/AOG orders. Creating a link
  // copies it to the clipboard for the broker to paste into their email; the
  // webhook marks the proforma paid automatically when the customer pays.
  const [cardLinkInfo, setCardLinkInfo] = useState<string | null>(null);
  const cardLinkMutation = useMutation({
    mutationFn: async () =>
      api<CardPaymentLinkResponse>(`/api/proforma-invoices/${pi.id}/card-payment-link`, {
        method: 'POST',
        tokenGetter: () => getToken(),
        body: JSON.stringify({}),
      }),
    onSuccess: async (data) => {
      try {
        await navigator.clipboard.writeText(data.url);
        setCardLinkInfo(
          `Link copied — charges ${fmtMoney(data.chargeCents, data.currency)} (invoice ${fmtMoney(data.invoiceCents, data.currency)} + ${fmtMoney(data.feeCents, data.currency)} card fee). Valid 24h.`,
        );
      } catch {
        setCardLinkInfo(`Link created (copy failed — use the link below): ${data.url}`);
      }
      qc.invalidateQueries({ queryKey: ['proforma-invoices'] });
    },
    onError: (err) => {
      setCardLinkInfo((err as Error)?.message ?? 'Could not create card link');
    },
  });

  // A previously-generated link is re-usable until its Stripe session expires
  // (~24h). Copying it is a pure client action — no new session, no server call —
  // so a broker who lost the clipboard copy gets it back without regenerating.
  const liveCardLink =
    pi.stripePaymentLinkUrl &&
    pi.stripeCheckoutExpiresAt &&
    new Date(pi.stripeCheckoutExpiresAt) > new Date()
      ? pi.stripePaymentLinkUrl
      : null;
  const copyExistingCardLink = async () => {
    if (!liveCardLink) return;
    try {
      await navigator.clipboard.writeText(liveCardLink);
      setCardLinkInfo(
        `Existing link copied${pi.stripeCheckoutExpiresAt ? ` — valid until ${new Date(pi.stripeCheckoutExpiresAt).toLocaleString()}` : ''}.`,
      );
    } catch {
      setCardLinkInfo(`Card link: ${liveCardLink}`);
    }
  };
  return (
    <li className="bg-amber-50/40 rounded p-2 text-sm border border-amber-100">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="font-mono text-xs font-semibold">
          {pi.ref}
          <span className={`ml-2 px-1.5 py-0.5 text-[10px] uppercase tracking-wide rounded ${PROFORMA_STATUS_BADGE[pi.status]}`}>
            {pi.status}
          </span>
          {isPastValid && (
            <span className="ml-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wide rounded bg-red-100 text-red-900">
              past validity
            </span>
          )}
        </div>
        <div className="flex items-baseline gap-3">
          <div className="font-mono">
            {fmtMoney(pi.totalCents, pi.currency)}
          </div>
          {pi.status === 'draft' && sourceQuote && (
            <button
              onClick={() => setEditOpen(true)}
              className="text-xs font-medium text-primary hover:underline"
            >
              Edit draft →
            </button>
          )}
          {canResolve && liveCardLink && (
            <button
              onClick={copyExistingCardLink}
              className="text-xs font-medium text-violet-700 hover:underline"
              title="Copy the existing card-payment link (still valid) without generating a new one"
            >
              Copy link →
            </button>
          )}
          {canResolve && (
            <button
              onClick={() => cardLinkMutation.mutate()}
              disabled={cardLinkMutation.isPending}
              className="text-xs font-medium text-violet-700 hover:underline disabled:opacity-50"
              title={liveCardLink
                ? 'Generate a fresh card link (expires the current one)'
                : 'Create a Stripe card-payment link (invoice + processing fee) and copy it'}
            >
              {cardLinkMutation.isPending ? 'Creating…' : liveCardLink ? 'New link →' : 'Card link →'}
            </button>
          )}
          {canResolve && (
            <button
              onClick={() => setPayOpen((v) => !v)}
              className="text-xs font-medium text-emerald-700 hover:underline"
            >
              {payOpen ? 'Close' : 'Mark paid →'}
            </button>
          )}
          {pi.status === 'paid' && !hasShipment && (
            <button
              onClick={() => setShipmentOpen(true)}
              className="text-xs font-medium text-primary hover:underline"
            >
              Record shipment →
            </button>
          )}
        </div>
      </div>

      {cardLinkInfo && (
        <div className="mt-1.5 text-[11px] text-violet-800 bg-violet-50 border border-violet-100 rounded px-2 py-1 break-all">
          {cardLinkInfo}
        </div>
      )}

      {canResolve && payOpen && (
        <div className="mt-2 space-y-1.5 border border-border rounded p-2 bg-background">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
            Payment received via
          </div>
          {pi.status === 'expired' && (
            <div className="text-[11px] text-amber-700">
              This proforma lapsed on {new Date(pi.validUntil).toLocaleDateString()}. Confirm the
              amount received still matches before marking it paid.
            </div>
          )}
          <select
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            className="w-full px-2 py-1 text-sm bg-background border border-border rounded"
          >
            <option value="wire">Wire (Revolut)</option>
            <option value="wise">Wise</option>
            <option value="ach">ACH</option>
            <option value="card">Credit card</option>
            <option value="stripe">Stripe</option>
            <option value="other">Other</option>
          </select>
          <input
            type="text"
            value={paymentReference}
            onChange={(e) => setPaymentReference(e.target.value)}
            placeholder="Wire ref / txn id (optional)"
            maxLength={200}
            className="w-full px-2 py-1 text-sm bg-background border border-border rounded font-mono"
          />
          {patchMutation.isError && (
            <div className="text-[11px] text-red-600">
              {(patchMutation.error as Error)?.message ?? 'Update failed'}
            </div>
          )}
          <button
            onClick={() => patchMutation.mutate('paid')}
            disabled={patchMutation.isPending}
            className="w-full px-3 py-2 text-sm font-medium bg-emerald-600 text-white rounded-md disabled:opacity-50"
          >
            {patchMutation.isPending ? 'Updating…' : `Mark as paid (${fmtMoney(pi.totalCents, pi.currency)})`}
          </button>
          <button
            onClick={() => patchMutation.mutate('cancelled')}
            disabled={patchMutation.isPending}
            className="w-full px-3 py-2 text-sm border border-border rounded-md hover:bg-muted"
          >
            Mark as cancelled
          </button>
        </div>
      )}
      <div className="mt-1 flex flex-wrap gap-1.5 text-[11px] text-muted-foreground">
        <span>quote {pi.quoteRef}</span>
        {pi.customerPoNumber && <><span>·</span><span>PO {pi.customerPoNumber}</span></>}
        <span>·</span>
        <span>issued {new Date(pi.generatedAt).toLocaleDateString()}</span>
        {pi.sentAt && <><span>·</span><span>sent {new Date(pi.sentAt).toLocaleDateString()}</span></>}
        {pi.paidAt && <><span>·</span><span className="font-semibold text-emerald-700">paid {new Date(pi.paidAt).toLocaleDateString()}{pi.paymentMethod ? ` via ${pi.paymentMethod}` : ''}</span></>}
        {!pi.paidAt && <><span>·</span><span>valid until {new Date(pi.validUntil).toLocaleDateString()}</span></>}
      </div>

      <ShipmentModal
        open={shipmentOpen}
        onClose={() => setShipmentOpen(false)}
        proforma={pi}
        rfqId={rfqId}
      />
      {sourceQuote && (
        <ProformaInvoiceModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          clientQuote={sourceQuote}
          rfqId={rfqId}
          editProformaId={pi.id}
          rfqCustomerPoNumber={rfqCustomerPoNumber}
          customerCountryCode={customerCountryCode}
          customerBisScreenStatus={customerBisScreenStatus}
        />
      )}
    </li>
  );
}

const SHIPMENT_STATUS_BADGE: Record<ShipmentStatus, string> = {
  preparing: 'bg-muted text-muted-foreground',
  shipped: 'bg-blue-100 text-blue-900',
  in_transit: 'bg-indigo-100 text-indigo-900',
  delivered: 'bg-emerald-100 text-emerald-900',
  exception: 'bg-red-100 text-red-900',
};

function ShipmentRow({
  shp,
  proforma,
  rfqId,
  hasCommercialInvoice,
}: {
  shp: Shipment;
  proforma: ProformaInvoice | null;
  rfqId: string;
  hasCommercialInvoice: boolean;
}) {
  const [editOpen, setEditOpen] = useState(false);
  const [ciOpen, setCiOpen] = useState(false);
  // A CI can be issued — or reissued — any time the shipment has actually moved.
  // Reissuing creates a corrected -Rn version and auto-supersedes the prior
  // (server-side), so there's always exactly one live document.
  const canIssueCi =
    proforma &&
    (shp.status === 'shipped' || shp.status === 'in_transit' || shp.status === 'delivered');
  return (
    <li className="bg-indigo-50/40 rounded p-2 text-sm border border-indigo-100">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="font-mono text-xs font-semibold">
          {shp.ref}
          <span className={`ml-2 px-1.5 py-0.5 text-[10px] uppercase tracking-wide rounded ${SHIPMENT_STATUS_BADGE[shp.status]}`}>
            {shp.status.replace(/_/g, ' ')}
          </span>
        </div>
        <div className="flex items-baseline gap-3">
          {shp.carrier && <div className="text-xs text-muted-foreground">{shp.carrier}</div>}
          <button
            onClick={() => setEditOpen(true)}
            className="text-xs font-medium text-primary hover:underline"
          >
            Edit →
          </button>
          {canIssueCi && (
            <button
              onClick={() => setCiOpen(true)}
              className="text-xs font-medium text-primary hover:underline"
            >
              {hasCommercialInvoice ? 'Reissue CI →' : 'Issue CI →'}
            </button>
          )}
          {/* A CI can only be issued once the shipment has moved. Rather than
              silently hide the control (broker can't tell why), show a disabled
              hint pointing at the unblock step. */}
          {proforma && !canIssueCi && (
            <span
              className="text-xs font-medium text-muted-foreground cursor-help"
              title="Mark the shipment 'shipped' (via Edit →) before issuing a commercial invoice."
            >
              Issue CI (mark shipped first)
            </span>
          )}
        </div>
      </div>
      <div className="mt-1 flex flex-wrap gap-1.5 text-[11px] text-muted-foreground">
        <span>proforma {shp.proformaRef}</span>
        {shp.vendorPoRef && <><span>·</span><span>vPO {shp.vendorPoRef}</span></>}
        {shp.awbOrTracking && <><span>·</span><span className="font-mono">{shp.awbOrTracking}</span></>}
        {shp.aesItn && <><span>·</span><span className="font-mono">AES {shp.aesItn}</span></>}
        {shp.shipDate && <><span>·</span><span>shipped {new Date(shp.shipDate).toLocaleDateString()}</span></>}
        {shp.deliveredAt && (
          <>
            <span>·</span>
            <span className="font-semibold text-emerald-700">delivered {new Date(shp.deliveredAt).toLocaleDateString()}</span>
          </>
        )}
      </div>

      {proforma && (
        <ShipmentModal
          open={editOpen}
          onClose={() => setEditOpen(false)}
          proforma={proforma}
          shipment={shp}
          rfqId={rfqId}
        />
      )}
      {proforma && (
        <CommercialInvoiceModal
          open={ciOpen}
          onClose={() => setCiOpen(false)}
          proforma={proforma}
          shipment={shp}
          rfqId={rfqId}
        />
      )}
    </li>
  );
}

const CI_STATUS_BADGE: Record<CommercialInvoiceStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  issued: 'bg-emerald-100 text-emerald-900',
  superseded: 'bg-amber-100 text-amber-900',
};

function CommercialInvoiceRow({ ci }: { ci: CommercialInvoice }) {
  const { getToken } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The list endpoint omits the html snapshot to stay lightweight, so fetch the
  // full document on demand (like VendorPoRow). The snapshot is frozen at issue
  // time and stored server-side, so a re-view always returns the exact doc.
  const openInNewTab = async () => {
    setError(null);
    // Pop the window synchronously so the click gesture isn't lost to the await
    // (Safari/Firefox block window.open inside a promise callback otherwise).
    const win = window.open('', '_blank');
    if (!win) {
      setError('Pop-up blocked — allow pop-ups for this site to open the document.');
      return;
    }
    try {
      setBusy(true);
      const data = await api<GetCommercialInvoiceResponse>(`/api/commercial-invoices/${ci.id}`, {
        tokenGetter: () => getToken(),
      });
      const html = data.commercialInvoice.html;
      if (!html) {
        setError('No document snapshot on this CI');
        win.close();
        return;
      }
      win.document.open();
      win.document.write(html);
      win.document.close();
    } catch (e) {
      win?.close();
      setError((e as { message?: string })?.message ?? 'Failed to load commercial invoice');
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="bg-emerald-50/40 rounded p-2 text-sm border border-emerald-100">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="font-mono text-xs font-semibold">
          {ci.ref}
          <span className={`ml-2 px-1.5 py-0.5 text-[10px] uppercase tracking-wide rounded ${CI_STATUS_BADGE[ci.status]}`}>
            {ci.status}
          </span>
        </div>
        <div className="flex items-baseline gap-3">
          <div className="font-mono">{fmtMoney(ci.totalCents, ci.currency)}</div>
          <button
            onClick={openInNewTab}
            disabled={busy}
            className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
          >
            {busy ? 'Opening…' : 'Open ↗'}
          </button>
        </div>
      </div>
      {error && <div className="mt-1 text-[11px] text-destructive">{error}</div>}
      <div className="mt-1 flex flex-wrap gap-1.5 text-[11px] text-muted-foreground">
        <span>shipment {ci.shipmentRef}</span>
        {ci.htsCode && <><span>·</span><span>HTS {ci.htsCode}</span></>}
        {ci.eccn && <><span>·</span><span>ECCN {ci.eccn}</span></>}
        {ci.countryOfOrigin && <><span>·</span><span>origin {ci.countryOfOrigin}</span></>}
        {ci.licenseCitation && <><span>·</span><span>license {ci.licenseCitation}</span></>}
        {ci.issuedAt && <><span>·</span><span>issued {new Date(ci.issuedAt).toLocaleDateString()}</span></>}
      </div>
    </li>
  );
}

// ===========================================================================
// VendorPoRow — read-out for a vendor PO attached to a line. Lets the broker
// re-view the PO HTML (fetched on-demand from GET /:id) and mark a draft
// "sent" (which advances the line stage 'accepted' → 'po_sent' server-side).
// ===========================================================================

const VENDOR_PO_STATUS_BADGE: Record<VendorPoStatus, string> = {
  draft: 'bg-muted text-muted-foreground',
  sent: 'bg-emerald-100 text-emerald-900',
  cancelled: 'bg-red-100 text-red-900',
};

function VendorPoRow({ vpo, rfqId }: { vpo: VendorPurchaseOrder; rfqId: string }) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const [poEditOpen, setPoEditOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const openInNewTab = async () => {
    setError(null);
    try {
      setBusy(true);
      const data = await api<GetVendorPoResponse>(`/api/vendor-purchase-orders/${vpo.id}`, {
        tokenGetter: () => getToken(),
      });
      if (!data.vendorPurchaseOrder.html) {
        setError('No document on this PO');
        return;
      }
      const win = window.open('', '_blank');
      if (!win) return;
      win.document.open();
      win.document.write(data.vendorPurchaseOrder.html);
      win.document.close();
    } catch (e) {
      setError((e as { message?: string })?.message ?? 'Failed to load PO');
    } finally {
      setBusy(false);
    }
  };

  const markSent = async () => {
    setError(null);
    try {
      setBusy(true);
      await api<PatchVendorPoResponse>(`/api/vendor-purchase-orders/${vpo.id}`, {
        method: 'PATCH',
        tokenGetter: () => getToken(),
        body: JSON.stringify({ status: 'sent' }),
      });
      qc.invalidateQueries({ queryKey: ['vendor-purchase-orders'] });
      qc.invalidateQueries({ queryKey: ['rfq', rfqId] });
      qc.invalidateQueries({ queryKey: ['rfqs'] });
    } catch (e) {
      setError((e as { message?: string })?.message ?? 'Failed to mark sent');
    } finally {
      setBusy(false);
    }
  };

  // Cancel a draft or already-sent PO. Backend allows both; without a row
  // control the only place to cancel was the create-session modal (a dead-end
  // once it closed). Cancelling a sent PO does NOT regress the line stage.
  const cancelPo = async () => {
    if (!window.confirm(`Cancel vendor PO ${vpo.ref}? This can't be undone.`)) return;
    setError(null);
    try {
      setBusy(true);
      await api<PatchVendorPoResponse>(`/api/vendor-purchase-orders/${vpo.id}`, {
        method: 'PATCH',
        tokenGetter: () => getToken(),
        body: JSON.stringify({ status: 'cancelled' }),
      });
      qc.invalidateQueries({ queryKey: ['vendor-purchase-orders'] });
      qc.invalidateQueries({ queryKey: ['rfq', rfqId] });
      qc.invalidateQueries({ queryKey: ['rfqs'] });
    } catch (e) {
      setError((e as { message?: string })?.message ?? 'Failed to cancel');
    } finally {
      setBusy(false);
    }
  };

  // Discard a mistaken draft entirely (draft-only on the backend).
  const deleteDraft = async () => {
    if (!window.confirm(`Delete draft vendor PO ${vpo.ref}? This removes it permanently.`)) return;
    setError(null);
    try {
      setBusy(true);
      await api<DeleteVendorPoResponse>(`/api/vendor-purchase-orders/${vpo.id}`, {
        method: 'DELETE',
        tokenGetter: () => getToken(),
      });
      qc.invalidateQueries({ queryKey: ['vendor-purchase-orders'] });
      qc.invalidateQueries({ queryKey: ['rfq', rfqId] });
      qc.invalidateQueries({ queryKey: ['rfqs'] });
    } catch (e) {
      setError((e as { message?: string })?.message ?? 'Failed to delete');
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="bg-violet-50/40 rounded p-2 text-sm border border-violet-100">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="font-mono text-xs font-semibold">
          {vpo.ref}
          <span className={`ml-2 px-1.5 py-0.5 text-[10px] uppercase tracking-wide rounded ${VENDOR_PO_STATUS_BADGE[vpo.status]}`}>
            {vpo.status}
          </span>
        </div>
        <div className="flex items-baseline gap-3">
          <div className="font-mono">{vpo.currency} {(vpo.vendorTotalCents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          <button
            onClick={openInNewTab}
            disabled={busy}
            className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
          >
            View ↗
          </button>
          {/* Drafts regenerate in place; a sent PO is amended into a new
              revision that keeps the number and preserves what was sent. */}
          {vpo.status !== 'cancelled' && (
            <button
              onClick={() => setPoEditOpen(true)}
              disabled={busy}
              className="text-xs font-medium text-foreground-muted hover:text-primary hover:underline disabled:opacity-50"
              title={vpo.status === 'draft'
                ? 'Edit this draft PO'
                : 'Create a revision — the PO number stays the same and the sent document is preserved'}
            >
              {vpo.status === 'draft' ? 'Edit' : 'Revise'}
            </button>
          )}
          {(vpo.revision ?? 1) > 1 && (
            <span className="pill pill-info" title="This PO has been amended">
              Rev {String.fromCharCode(64 + (vpo.revision ?? 1))}
            </span>
          )}
          {vpo.status === 'draft' && (
            <button
              onClick={markSent}
              disabled={busy}
              className="text-xs font-medium text-emerald-700 hover:underline disabled:opacity-50"
              title="Marks the PO as sent and advances the line stage from 'accepted' to 'po_sent'"
            >
              Mark sent →
            </button>
          )}
          {(vpo.status === 'draft' || vpo.status === 'sent') && (
            <button
              onClick={cancelPo}
              disabled={busy}
              className="text-xs font-medium text-red-700 hover:underline disabled:opacity-50"
            >
              Cancel
            </button>
          )}
          {vpo.status === 'draft' && (
            <button
              onClick={deleteDraft}
              disabled={busy}
              className="text-xs font-medium text-muted-foreground hover:underline disabled:opacity-50"
            >
              Delete
            </button>
          )}
        </div>
      </div>
      <div className="mt-1 flex flex-wrap gap-1.5 text-[11px] text-muted-foreground">
        <span>{vpo.vendorName ?? 'vendor'}</span>
        <span>·</span>
        <span>{vpo.shipToMode === 'customer_direct' ? 'drop-ship to customer' : 'ship to APD'}</span>
        <span>·</span>
        <span>issued {new Date(vpo.generatedAt).toLocaleDateString()}</span>
        {vpo.sentAt && <><span>·</span><span>sent {new Date(vpo.sentAt).toLocaleDateString()}</span></>}
        {vpo.cancelledAt && <><span>·</span><span className="text-red-700">cancelled {new Date(vpo.cancelledAt).toLocaleDateString()}</span></>}
      </div>
      {error && <div className="text-xs text-destructive mt-1">{error}</div>}
    {poEditOpen && (
        <EditVendorPoModal po={vpo} onClose={() => setPoEditOpen(false)} />
      )}
      </li>
  );
}
